import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveIndex] = useState(null); // 'varmepumper' | 'el' | 'sikring' | null
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveIndex(null);
  }, [location]);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveIndex(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems = [
    { label: 'Forside', href: '/' },
    {
      label: 'Varmepumper',
      href: '/varmepumper/daikin-luft-til-vand',
      id: 'varmepumper',
      children: [
        { label: 'Daikin Luft til Vand', href: '/varmepumper/daikin-luft-til-vand' },
        { label: 'Luft til Luft Varmepumper', href: '/varmepumper/luft-til-luft' },
        { label: 'Service & Vedligehold', href: '/varmepumper/service-og-vedligehold' },
        { label: 'Tilskud & Finansiering', href: '/varmepumper/tilskud-og-finansiering' },
      ],
    },
    {
      label: 'El-installation',
      href: '/el-installation/traditionelt-el-arbejde',
      id: 'el',
      children: [
        { label: 'Traditionelt El-arbejde', href: '/el-installation/traditionelt-el-arbejde' },
        { label: 'Ladebokse til Elbil', href: '/el-installation/ladebokse-til-elbil' },
        { label: 'IHC & Smart Home', href: '/el-installation/ihc-og-smart-home' },
      ],
    },
    {
      label: 'Sikring & Netværk',
      href: '/sikring-og-netvaerk/alarmsystemer-og-overvaagning',
      id: 'sikring',
      children: [
        { label: 'Alarmsystemer & Overvågning', href: '/sikring-og-netvaerk/alarmsystemer-og-overvaagning' },
        { label: 'Teledata & Netværk', href: '/sikring-og-netvaerk/teledata-og-netvaerk' },
      ],
    },
    { label: 'Cases & Referencer', href: '/cases' },
    { label: 'Om os', href: '/om-os' },
  ];

  const handleDropdownToggle = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveIndex(activeDropdown === id ? null : id);
  };

  const isChildActive = (children) => {
    return children.some(child => location.pathname === child.href);
  };

  return (
    <header ref={dropdownRef} className="sticky top-0 z-50 w-full border-b border-navy-100 bg-white/95 shadow-sm backdrop-blur-md">
      {/* Top bar with contact info */}
      <div className="bg-navy-900 py-2 text-xs text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <a href="tel:+4586667700" className="flex items-center gap-1.5 hover:text-energy-400 transition-colors">
              <Phone className="h-3.5 w-3.5 text-energy-500" />
              <span className="whitespace-nowrap">+45 86 66 77 00</span>
            </a>
            <a href="mailto:post@detekniq.dk" className="flex items-center gap-1.5 hover:text-energy-400 transition-colors">
              <Mail className="h-3.5 w-3.5 text-energy-500" />
              <span className="whitespace-nowrap">post@detekniq.dk</span>
            </a>
          </div>
          <div className="hidden items-center gap-1.5 sm:flex">
            <MapPin className="h-3.5 w-3.5 text-energy-500" />
            <span>Nørrevangsvej 5, 8620 Kjellerup</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 lg:h-32 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://assets.auxodev.dk/assets/c02e6411-90cc-481d-8aeb-e9d9691b4c94/de-logo-vek2-hvid2.png"
              alt="D.E. TekniQ K/S Logo"
              className="h-16 md:h-20 lg:h-[135px] lg:translate-y-3 w-auto transition-all"
            />
          </Link>

          {/* Desktop Menu (Visible >= 1200px) */}
          <nav className="hidden items-center gap-1 min-[1200px]:flex">
            {menuItems.map((item) => {
              if (item.children) {
                const hasActiveChild = isChildActive(item.children);
                return (
                  <div key={item.label} className="relative group">
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        `flex items-center gap-1 rounded-none px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-energy-500 ${
                          isActive || hasActiveChild
                            ? 'bg-navy-50 text-navy-950 font-semibold'
                            : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                        }`
                      }
                    >
                      {item.label}
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                    </NavLink>

                    {/* Dropdown Menu (Opens on hover) */}
                    <div className="absolute left-0 mt-0 pt-2 w-64 hidden group-hover:block z-50">
                      <div className="rounded-none border border-navy-100 bg-white p-2 shadow-xl ring-1 ring-navy-950/5">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block rounded-none px-4 py-2.5 text-sm transition-colors ${
                              location.pathname === child.href
                                ? 'bg-energy-50 font-semibold text-energy-700'
                                : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.href}
                  className={({ isActive }) =>
                    `rounded-none px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-navy-50 text-navy-950 font-semibold'
                        : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            {/* Contact CTA Button */}
            <Link
              to="/kontakt"
              className="ml-4 rounded-none bg-energy-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:bg-energy-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-energy-500 focus:ring-offset-2"
            >
              Kontakt os
            </Link>
          </nav>

          {/* Burgermenu Button (Visible < 1200px) */}
          <div className="flex items-center min-[1200px]:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-none p-2.5 text-navy-950 hover:bg-navy-50 focus:outline-none focus:ring-2 focus:ring-energy-500"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel (Visible < 1200px) */}
      {mobileMenuOpen && (
        <div className="border-t border-navy-100 bg-white py-4 shadow-inner min-[1200px]:hidden max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="space-y-1 px-4">
            {menuItems.map((item) => {
              if (item.children) {
                const hasActiveChild = isChildActive(item.children);
                const isOpen = activeDropdown === item.id;
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={(e) => handleDropdownToggle(item.id, e)}
                      className={`flex w-full items-center justify-between rounded-none px-4 py-3 text-base font-medium transition-colors ${
                        hasActiveChild || location.pathname === item.href
                          ? 'bg-navy-50 text-navy-950 font-semibold'
                          : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="pl-4 space-y-1 border-l-2 border-navy-100 ml-4">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setActiveIndex(null);
                            }}
                            className={`block rounded-none px-4 py-2.5 text-sm transition-colors ${
                              location.pathname === child.href
                                ? 'bg-energy-50 font-semibold text-energy-700'
                                : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.href}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setActiveIndex(null);
                  }}
                  className={({ isActive }) =>
                    `block rounded-none px-4 py-3 text-base font-medium transition-colors ${
                      isActive
                        ? 'bg-navy-50 text-navy-950 font-semibold'
                        : 'text-graphite-700 hover:bg-navy-50 hover:text-navy-950'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            {/* Mobile Contact CTA */}
            <div className="pt-4">
              <Link
                to="/kontakt"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setActiveIndex(null);
                }}
                className="block w-full text-center rounded-none bg-energy-600 py-3 text-base font-semibold text-white shadow-md transition-colors hover:bg-energy-700"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
